// Instrument MUST be the very first import — it initialises Sentry
// before React itself runs, so React internals get instrumented too.
// Do NOT reorder these imports.
import './instrument';
import { Sentry } from './instrument';
import React from 'react';
import ReactDOM from 'react-dom/client';
import '@/index.css';
import App from '@/App';

/**
 * Fallback UI when a render crashes. Kept intentionally minimal so it
 * still renders even if the theme provider / context tree that would
 * normally style it has also failed. The reload button re-mounts the
 * entire app tree — cheap escape hatch that recovers from most render
 * bugs (stale ref, race, corrupted local state).
 */
function AppCrashFallback({ eventId, resetError }) {
  return (
    <div style={{ padding: 32, fontFamily: 'system-ui, sans-serif', maxWidth: 480, margin: '10vh auto', textAlign: 'center' }}>
      <h1 style={{ fontSize: 22, marginBottom: 8 }}>Something went wrong</h1>
      <p style={{ color: '#555', marginBottom: 16 }}>
        We&apos;ve been notified and are looking into it. You can try reloading — most times that gets you unstuck.
      </p>
      {eventId ? (
        <p style={{ fontSize: 12, color: '#888', marginBottom: 20 }}>
          Reference: <code>{eventId}</code>
        </p>
      ) : null}
      <button
        type="button"
        onClick={() => { if (typeof resetError === 'function') resetError(); window.location.reload(); }}
        data-testid="app-crash-reload-btn"
        style={{
          padding: '10px 22px',
          borderRadius: 999,
          background: '#f59e0b',
          color: '#fff',
          border: 0,
          fontWeight: 600,
          cursor: 'pointer',
        }}
      >
        Reload the app
      </button>
    </div>
  );
}

// Global unhandled promise rejection reporter. Sentry auto-captures
// these; this listener exists to surface a console line in dev so we
// don't miss them locally where Sentry may be disabled.
if (typeof window !== 'undefined') {
  window.addEventListener('unhandledrejection', (event) => {
    console.warn('[unhandledrejection]', event.reason);
  });
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={AppCrashFallback} showDialog={false}>
      <App />
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
);
